import React from 'react';
import { cn } from '@/lib/utils';

export interface RadioOption {
  value: string;
  label: string;
  hint?: string;
  disabled?: boolean;
  /** Optional CSS class(es) applied to the label element for per-option styling (e.g. 'text-destructive'). */
  labelClassName?: string;
}

export interface RadioGroupProps {
  name?: string;
  options: RadioOption[];
  value?: string;
  onChange?: (value: string) => void;
  label?: string;
  error?: string;
  hint?: string;
  className?: string;
  orientation?: 'horizontal' | 'vertical';
}

export const RadioGroup = React.forwardRef<HTMLDivElement, RadioGroupProps>(
  (
    {
      name,
      options,
      value,
      onChange,
      label,
      error,
      hint,
      className,
      orientation = 'vertical',
    },
    ref
  ) => {
    const groupId = `radio-group-${Math.random().toString(36).substr(2, 9)}`;
    const resolvedName = name ?? groupId;
    const hasError = Boolean(error);
    
    return (
      <div ref={ref} className={cn('w-full', className)}>
        {label && (
          <p className="text-sm font-medium text-foreground mb-2">
            {label}
          </p>
        )}
        
        <div
          className={cn(
            'flex gap-4',
            orientation === 'vertical' ? 'flex-col' : 'flex-row flex-wrap'
          )}
          role="radiogroup"
          aria-labelledby={label ? groupId : undefined}
          aria-invalid={hasError}
          aria-describedby={hasError ? `${groupId}-error` : hint ? `${groupId}-hint` : undefined}
        >
          {options.map((option) => {
            const optionId = `${groupId}-${option.value}`;
            return (
              <div key={option.value} className="flex items-start">
                <input
                  id={optionId}
                  type="radio"
                  name={resolvedName}
                  value={option.value}
                  checked={value === option.value}
                  onChange={() => onChange?.(option.value)}
                  disabled={option.disabled}
                  className={cn(
                    'h-4 w-4 mt-0.5 border border-input bg-background text-primary-600 focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
                    hasError && 'border-error-500'
                  )}
                />
                <div className="ml-2">
                  <label
                    htmlFor={optionId}
                    className={cn('text-sm font-medium text-foreground cursor-pointer select-none', option.labelClassName)}
                  >
                    {option.label}
                  </label>
                  {option.hint && (
                    <p className="text-sm text-muted-foreground mt-0.5">
                      {option.hint}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        
        {hint && !error && (
          <p id={`${groupId}-hint`} className="mt-2 text-sm text-muted-foreground">
            {hint}
          </p>
        )}
        
        {error && (
          <p id={`${groupId}-error`} className="mt-2 text-sm text-error-600">
            {error}
          </p>
        )}
      </div>
    );
  }
);

RadioGroup.displayName = 'RadioGroup';
